import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calendar, 
  Clock, 
  Search,
  Phone,
  Video,
  FileText,
  CheckCircle,
  XCircle,
  ChevronLeft,
  Filter,
  MoreVertical,
  RefreshCw
} from 'lucide-react';
import { format, isToday, isTomorrow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { appointmentService, getLocalizedValue } from '@/services/api';

// Status mapping for appointments
const APPOINTMENT_STATUS = {
  1: { key: 'pending', en: 'Pending', ar: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-800' },
  2: { key: 'confirmed', en: 'Confirmed', ar: 'مؤكد', color: 'bg-blue-100 text-blue-800' },
  3: { key: 'on_the_way', en: 'On the Way', ar: 'في الطريق', color: 'bg-purple-100 text-purple-800' },
  4: { key: 'in_progress', en: 'In Progress', ar: 'جاري', color: 'bg-teal-100 text-teal-800' },
  5: { key: 'completed', en: 'Completed', ar: 'مكتمل', color: 'bg-green-100 text-green-800' },
  6: { key: 'cancelled', en: 'Cancelled', ar: 'ملغي', color: 'bg-red-100 text-red-800' },
  7: { key: 'waiting', en: 'Waiting', ar: 'انتظار', color: 'bg-orange-100 text-orange-800' },
};

const DoctorAppointments = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await appointmentService.getAllAppointments();
      const rawData = response.data?.data || response.data || [];
      
      // Transform API data to our format
      const transformedAppointments = rawData.map(apt => ({
        id: apt.id,
        patient: {
          id: apt.patient?.id || apt.patient_id,
          name: apt.patient 
            ? `${getLocalizedValue(apt.patient.first_name, lang) || ''} ${getLocalizedValue(apt.patient.last_name, lang) || ''}`.trim()
            : (lang === 'ar' ? 'مريض' : 'Patient'),
          phone: apt.patient?.phone_number || apt.patient?.mobile_number || '',
          image: apt.patient?.media?.[0]?.url || apt.patient?.media?.[0]?.thumb,
          age: apt.patient?.age,
          gender: apt.patient?.gender,
        },
        doctor: {
          id: apt.doctor?.id || apt.doctor_id,
          name: getLocalizedValue(apt.doctor?.name, lang) || '',
        },
        date: new Date(apt.appointment_at),
        time: format(new Date(apt.appointment_at), 'hh:mm a'),
        type: apt.online 
          ? (lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation')
          : (lang === 'ar' ? 'زيارة عيادة' : 'Clinic Visit'),
        status: APPOINTMENT_STATUS[apt.appointment_status_id]?.key || 'pending',
        statusId: apt.appointment_status_id,
        notes: apt.hint || '',
        online: apt.online,
        total: apt.total,
        clinic: apt.clinic ? getLocalizedValue(apt.clinic.name, lang) : '',
      }));
      
      setAppointments(transformedAppointments);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل المواعيد' : 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAppointments();
  }, [isAuthenticated, navigate, lang]);

  const filterAppointments = (tab) => {
    let filtered = appointments;
    
    if (searchQuery) {
      filtered = filtered.filter(apt => 
        apt.patient.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch (tab) {
      case 'today':
        return filtered.filter(apt => isToday(new Date(apt.date)));
      case 'upcoming':
        return filtered.filter(apt => new Date(apt.date) > new Date() && !isToday(new Date(apt.date)));
      case 'past':
        return filtered.filter(apt => new Date(apt.date) < new Date() && !isToday(new Date(apt.date)));
      default:
        return filtered;
    }
  };

  const handleCompleteAppointment = () => {
    if (selectedAppointment) {
      setAppointments(prev => prev.map(apt => 
        apt.id === selectedAppointment.id 
          ? { ...apt, status: 'completed', notes: visitNotes || apt.notes }
          : apt
      ));
      toast.success(lang === 'ar' ? 'تم إكمال الموعد' : 'Appointment completed');
      setCompleteDialogOpen(false);
      setSelectedAppointment(null);
      setVisitNotes('');
    }
  };

  const handleCancelAppointment = (apt) => {
    setAppointments(prev => prev.map(a => 
      a.id === apt.id ? { ...a, status: 'cancelled' } : a
    ));
    toast.success(lang === 'ar' ? 'تم إلغاء الموعد' : 'Appointment cancelled');
  };

  const getStatusBadge = (status, statusId) => {
    // Use the APPOINTMENT_STATUS mapping with statusId for accurate display
    if (statusId && APPOINTMENT_STATUS[statusId]) {
      const config = APPOINTMENT_STATUS[statusId];
      return <Badge className={config.color}>{lang === 'ar' ? config.ar : config.en}</Badge>;
    }
    
    // Fallback to status key
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700', label: lang === 'ar' ? 'معلق' : 'Pending' },
      confirmed: { color: 'bg-blue-100 text-blue-700', label: lang === 'ar' ? 'مؤكد' : 'Confirmed' },
      in_progress: { color: 'bg-teal-100 text-teal-700', label: lang === 'ar' ? 'جاري' : 'In Progress' },
      completed: { color: 'bg-green-100 text-green-700', label: lang === 'ar' ? 'مكتمل' : 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', label: lang === 'ar' ? 'ملغي' : 'Cancelled' },
      waiting: { color: 'bg-orange-100 text-orange-700', label: lang === 'ar' ? 'انتظار' : 'Waiting' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatAppointmentDate = (date) => {
    if (isToday(new Date(date))) {
      return lang === 'ar' ? 'اليوم' : 'Today';
    }
    if (isTomorrow(new Date(date))) {
      return lang === 'ar' ? 'غداً' : 'Tomorrow';
    }
    return format(new Date(date), 'EEEE, MMMM d', { locale });
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-appointments">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/dashboard')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {lang === 'ar' ? 'إدارة المواعيد' : 'Appointment Management'}
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'عرض وإدارة مواعيدك' : 'View and manage your appointments'}
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={lang === 'ar' ? 'البحث عن مريض...' : 'Search patients...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-appointments"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-3 mb-6">
            <TabsTrigger value="today">{lang === 'ar' ? 'اليوم' : 'Today'}</TabsTrigger>
            <TabsTrigger value="upcoming">{lang === 'ar' ? 'القادمة' : 'Upcoming'}</TabsTrigger>
            <TabsTrigger value="past">{lang === 'ar' ? 'السابقة' : 'Past'}</TabsTrigger>
          </TabsList>

          {['today', 'upcoming', 'past'].map(tab => (
            <TabsContent key={tab} value={tab} className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <Skeleton className="h-14 w-14 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-1/3" />
                            <Skeleton className="h-4 w-1/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filterAppointments(tab).length === 0 ? (
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardContent className="p-8 text-center">
                    <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد مواعيد' : 'No appointments found'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filterAppointments(tab).map(apt => (
                  <Card key={apt.id} className="dark:bg-slate-800 border-0 shadow-sm" data-testid={`appointment-${apt.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <Avatar className="h-14 w-14">
                          <AvatarImage src={apt.patient.image} />
                          <AvatarFallback className="bg-teal-100 text-teal-700">
                            {apt.patient.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-slate-900 dark:text-white">{apt.patient.name}</h3>
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                {apt.patient.age} {lang === 'ar' ? 'سنة' : 'years'} • {apt.patient.gender}
                              </p>
                            </div>
                            {getStatusBadge(apt.status, apt.statusId)}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-600 dark:text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatAppointmentDate(apt.date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {apt.time}
                            </span>
                          </div>
                          <p className="text-sm text-teal-600 mt-1">{apt.type}</p>
                          {apt.notes && (
                            <p className="text-sm text-slate-500 mt-2 bg-slate-50 p-2 rounded">
                              {apt.notes}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {apt.status !== 'cancelled' && apt.status !== 'completed' && (
                        <div className="flex gap-2 mt-4 pt-4 border-t">
                          <Button variant="outline" size="sm" className="flex-1">
                            <Phone className="h-4 w-4 mr-2" />
                            {lang === 'ar' ? 'اتصال' : 'Call'}
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1">
                            <Video className="h-4 w-4 mr-2" />
                            {lang === 'ar' ? 'فيديو' : 'Video'}
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setCompleteDialogOpen(true);
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {lang === 'ar' ? 'إكمال' : 'Complete'}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Complete Appointment Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lang === 'ar' ? 'إكمال الموعد' : 'Complete Appointment'}
            </DialogTitle>
            <DialogDescription>
              {lang === 'ar' 
                ? 'أضف ملاحظات الزيارة وأكمل الموعد'
                : 'Add visit notes and complete the appointment'
              }
            </DialogDescription>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedAppointment.patient.image} />
                  <AvatarFallback>{selectedAppointment.patient.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedAppointment.patient.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{selectedAppointment.type}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">
                  {lang === 'ar' ? 'ملاحظات الزيارة' : 'Visit Notes'}
                </label>
                <Textarea
                  placeholder={lang === 'ar' ? 'أدخل ملاحظات الزيارة...' : 'Enter visit notes...'}
                  value={visitNotes}
                  onChange={(e) => setVisitNotes(e.target.value)}
                  rows={4}
                  className="mt-2"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleCompleteAppointment}>
              {lang === 'ar' ? 'إكمال الموعد' : 'Complete Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorAppointments;
